import React, { useEffect, useState } from "react";
import { Paper } from "@mui/material";
import { useSelector } from "react-redux";
import styles from "./ReqDetails.module.css";
import ReqDetailsEmptyState from "./ReqDetailsEmptyState";
import ReqDetailsLoader from "./ReqDetailsLoader";
import { ReqInfoTable, Section, ReqReviewsTable, ReqPositionsTable } from "./components";
import { Flags, isFeatureEnabled, translate } from "../../../../../common/providers";
import { CustomIconButton, DeliverablesList, icon, Preview, SearchRow } from "../../../../../common/components";

export default function ReqDetails({ isLoadingDetails, onClickEdit, requirement }) {
	const projectId = useSelector(({ context }) => context.project.id || 0);
	const [openedSections, setOpenedSections] = useState([]);
	const [preview, setPreview] = useState(null);
	const {
		attributes,
		category,
		clause,
		clientRef,
		content,
		criticality,
		documentId,
		documentName,
		documentOuts,
		id,
		negotiability,
		page,
		positions,
		reviews,
		type,
		coordinates,
		coverageStatus,
		covered,
	} = requirement || {};
	useEffect(() => {
		const defaultDeliverables = (isFeatureEnabled(Flags.COVERAGE_PLUGIN) && ["DELIVERABLES"]) || [];
		setOpenedSections(["POSITIONS", "REVIEWS", "INFORMATION", ...defaultDeliverables]);
	}, []);
	const handleCollapse = (keyToOpen) => {
		if (keyToOpen) {
			setOpenedSections((prev) => {
				if (prev.includes(keyToOpen)) {
					return prev.filter((k) => k !== keyToOpen);
				}
				return [...prev, keyToOpen];
			});
		}
	};
	const handleCloseDisplay = () => setPreview(null);
	const handleDisplay = (infoId, docId, pageStart, infoType, rec) => {
		setPreview({
			coordinates: requirement.coordinates || rec || null,
			documentId: requirement.documentId || docId,
			documentName,
			clause,
			page: requirement.page || pageStart,
			informationId: requirement.id || infoId,
			informationType: infoType,
		});
	};
	return (
		<div className={styles["req-details"]}>
			{isLoadingDetails && <ReqDetailsLoader />}
			{!isLoadingDetails && !requirement && <ReqDetailsEmptyState />}
			{!isLoadingDetails && !!requirement && (
				<>
					<Paper square className={styles.details__header}>
						<SearchRow
							hasFolderIcon
							attributes={attributes}
							categories={category}
							clause={clause}
							content={content}
							coverageStatus={coverageStatus}
							covered={covered}
							documentId={documentId}
							documentName={documentName}
							informationId={id}
							informationType={type}
							isCheckboxDisplayed={false}
							page={page}
							reqId={id}
							row={{ coordinates }}
							onDisplay={handleDisplay}
						/>
					</Paper>
					<br />
					<Section
						collapsed={openedSections.includes("POSITIONS")}
						sectionTag="POSITIONS"
						title={translate("requirement-matrix.req-details.section.company-position")}
						onCollapse={handleCollapse}
					>
						<ReqPositionsTable positions={positions} />
					</Section>
					<Section
						collapsed={openedSections.includes("REVIEWS")}
						sectionTag="REVIEWS"
						title={translate("requirement-matrix.req-details.section.reviewer")}
						onCollapse={handleCollapse}
					>
						<ReqReviewsTable reviews={reviews} />
					</Section>
					<Section
						collapsed={openedSections.includes("INFORMATION")}
						options={[
							<CustomIconButton
								icon={icon.faPen}
								tooltip={translate("requirement-matrix.req-details.header.edit-req")}
								onClick={onClickEdit}
							/>,
						]}
						sectionTag="INFORMATION"
						title={translate("requirement-matrix.req-details.section.information")}
						onCollapse={handleCollapse}
					>
						<ReqInfoTable
							attributes={attributes}
							category={category}
							clause={clause}
							clientRef={clientRef}
							criticality={criticality}
							documentName={documentName}
							documentOuts={documentOuts}
							id={id}
							negotiability={negotiability}
							type={type}
						/>
					</Section>
					{isFeatureEnabled(Flags.COVERAGE_PLUGIN) && (
						<Section
							collapsed={openedSections.includes("DELIVERABLES")}
							elevation={0}
							sectionTag="DELIVERABLES"
							title={translate("requirement-matrix.req-details.section.related-deliverables")}
							onCollapse={handleCollapse}
						>
							<div className={styles.deliverablesList}>
								<DeliverablesList hasCoverage reqId={requirement.id} />
							</div>
						</Section>
					)}
				</>
			)}
			<Preview
				hasFolderIcon
				contentLocation={preview?.clause}
				coordinates={preview?.coordinates}
				docId={preview?.documentId}
				docName={preview?.documentName}
				infoId={preview?.informationId}
				infoType={preview?.informationType}
				open={!!preview}
				page={preview?.page}
				projectId={projectId}
				onClose={handleCloseDisplay}
			/>
		</div>
	);
}
